import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Responsive, WidthProvider } from 'react-grid-layout';
import classnames from 'classnames';
import sortBy from 'lodash/sortBy';
import differenceWith from 'lodash/differenceWith';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { DeleteOutline } from '@mui/icons-material';

import { OEM } from 'utility/OEMProvider';
import { setMiniSidenav, useMaterialUIController } from 'context';

import Sidenav from 'examples/Sidenav';
import { resources } from 'oems/resources';
import { Button } from 'components/ui';
import AccountMenu from 'features/accountMenu/accountMenu';

import * as Styled from './Main.styles';

const ResponsiveGridLayout = WidthProvider(Responsive);
const layoutBreakpoint = 1000;

export const Main = ({ children, routes }) => {
  const dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const { miniSidenav, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [editLayoutData, setEditLayoutData] = useState(null);
  const [currentLayoutWidth, setCurrentLayoutWidth] = useState(null);
  const [savedData, setSavedData] = useState([]);

  const allGridItems = useMemo(
    () => [
      {
        id: 'sampleButton',
        label: 'Sample Button',
        position: { w: 4, h: 2 },
        content: (
          <Styled.Box>
            <Button onClick={() => null}>Sample Button</Button>
          </Styled.Box>
        ),
      },
      {
        id: 'accountDropdown',
        label: 'Account Dropdown',
        position: { w: 4, h: 3 },
        content: (
          <Styled.Box>
            <AccountMenu />
          </Styled.Box>
        ),
      },
    ],
    [],
  );

  const [gridItems, setGridItems] = useState([]);

  useEffect(() => {
    setGridItems(
      (editLayoutData || savedData).map((item) => {
        return {
          ...(allGridItems.find((gridItemData) => gridItemData.id === item.id) || {}),
          ...item,
        };
      }),
    );
  }, [savedData, editLayoutData, setGridItems, allGridItems]);

  const layouts = useMemo(
    () => ({
      desktop: gridItems.map((gridItem) => ({
        i: gridItem.id,
        ...gridItem.position,
        static: !editLayoutData,
      })),
      mobile: sortBy(gridItems, [(gridItem) => gridItem.position.y, (gridItem) => gridItem.position.x]).reduce(
        (acc, gridItem, index) => {
          const lastItem = acc[index - 1];

          return [
            ...acc,
            {
              i: gridItem.id,
              ...gridItem.position,
              static: !editLayoutData,
              x: 0,
              y: lastItem ? lastItem.h + lastItem.y : 0,
              w: 1,
            },
          ];
        },
        [],
      ),
    }),
    [gridItems, editLayoutData],
  );

  const onLayoutChange = (layout) =>
    !!editLayoutData &&
    setEditLayoutData((old) =>
      (old || []).map((widget) => {
        const layoutItem = layout.find((layoutItem) => layoutItem.i === widget.id);

        return {
          ...widget,
          position: {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
          },
        };
      }),
    );

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  return (
    <Styled.Wrapper>
      <Sidenav
        color={sidenavColor}
        brand={resources.logoLightImage}
        brandMini={resources.logoLightMiniImage}
        brandName={OEM().ProductName}
        routes={routes}
        onMouseEnter={handleOnMouseEnter}
        onMouseLeave={handleOnMouseLeave}
      />

      {children}

     {/* {currentLayoutWidth > layoutBreakpoint && (
        <Styled.Buttons>
          {editLayoutData ? (
            <>
              {differenceWith(allGridItems, editLayoutData, (a, b) => a.id === b.id).map((item) => (
                <Button
                  key={item.id}
                  variant="text"
                  onClick={() =>
                    setEditLayoutData((old) => [
                      ...(old || []),
                      {
                        id: item.id,
                        position: {
                          w: item.position.w,
                          h: item.position.h,
                          x: 0,
                          y: Infinity,
                        },
                      },
                    ])
                  }
                >
                  + {item.label}
                </Button>
              ))}
              <Button
                variant="outlined"
                onClick={() => {
                  setEditLayoutData(null);
                }}
              >
                Anuluj
              </Button>
              <Button
                onClick={() => {
                  setSavedData(editLayoutData);
                  setEditLayoutData(null);
                }}
              >
                Zapisz
              </Button>
            </>
          ) : (
            <IconButton onClick={() => setEditLayoutData(savedData)}>
              <EditIcon />
            </IconButton>
          )}
        </Styled.Buttons>
      )}
      */}
      <Styled.LayoutWrapper className={classnames({ isEditing: !!editLayoutData })}>
        <ResponsiveGridLayout
          layouts={{ desktop: layouts.desktop, mobile: layouts.mobile }}
          breakpoints={{ desktop: 1000, mobile: 0 }}
          cols={{ desktop: 12, mobile: 1 }}
          rowHeight={60}
          onLayoutChange={onLayoutChange}
          onWidthChange={(width) => {
            setCurrentLayoutWidth(width);
            setEditLayoutData(null);
          }}
        >
          {gridItems?.map((gridItem) => (
            <div key={gridItem.id} className={classnames('item')}>
              {gridItem.content}
              {editLayoutData && !gridItem.required && (
                <Button
                  onClick={() =>
                    setEditLayoutData((old) => (old || []).filter((layoutItem) => layoutItem.id !== gridItem.id))
                  }
                  className="deleteButton"
                  variant="text"
                  color="error"
                >
                  <DeleteOutline />
                </Button>
              )}
            </div>
          ))}
        </ResponsiveGridLayout>
      </Styled.LayoutWrapper>
    </Styled.Wrapper>
  );
};

Main.propTypes = {
  routes: PropTypes.array,
  children: PropTypes.node,
};
